import API from '../../../lib/commons/api'

const UI_STATE_PATH = '/api/v1/ui_states'

/**
 * 画面(applicationFunctionUuid)に属さず、アプリ全体で共通に使う UI 状態のキー。
 * 画面ごとの UI 状態は画面側のキーと混ざらないよう applicationFunctionUuid つきで保存する。
 */
export type GlobalUiStateKey = 'AI_AGENT_CHAT_FAB_DISMISSED'

// ログインユーザー単位で保存する。テナント共通にしたい設定は現れていないため USER のみ扱う。
const USER_SCOPE = 'USER'

/**
 * ログインユーザーのアプリ全体共通の UI 状態を取得する。
 *
 * applicationFunctionUuid を送らないことで、保存先が特定の画面に紐づかなくなる。
 */
export const fetchGlobalUiState = async <T>(
  key: GlobalUiStateKey
): Promise<T | undefined> => {
  const response = await API.presentational.request('GET', UI_STATE_PATH, {
    key,
    scope: USER_SCOPE,
  })
  if (!response.json?.value) return undefined
  return JSON.parse(response.json.value) as T
}

export const saveGlobalUiState = async <T>(
  key: GlobalUiStateKey,
  value: T
): Promise<void> => {
  await API.presentational.request('POST', UI_STATE_PATH, {
    key,
    scope: USER_SCOPE,
    value: JSON.stringify(value),
  })
}

/**
 * ログインユーザーの、画面(applicationFunctionUuid)ごとの UI 状態を取得する。
 *
 * 共通 API クライアントは失敗しても空の応答で解決し、未保存と区別できないため、失敗したら例外を投げる。
 */
export const fetchUiState = async <T>(
  applicationFunctionUuid: string,
  key: string
): Promise<T | undefined> => {
  let isFailed = false
  const response = await API.presentational.request(
    'GET',
    UI_STATE_PATH,
    { applicationFunctionUuid, key, scope: USER_SCOPE },
    true,
    null,
    {},
    (err, input) => {
      isFailed = true
      API.presentational.defaultErrorCallback(err, input)
    }
  )
  if (isFailed) throw new Error(`Failed to fetch ui state: ${key}`)
  if (!response.json?.value) return undefined
  return JSON.parse(response.json.value) as T
}

export const saveUiState = async <T>(
  applicationFunctionUuid: string,
  key: string,
  value: T
): Promise<void> => {
  await API.presentational.request(
    'POST',
    `${UI_STATE_PATH}?applicationFunctionUuid=${applicationFunctionUuid}`,
    { key, scope: USER_SCOPE, value: JSON.stringify(value) }
  )
}
